import {isEqual, uniq} from 'lodash';
import {useEffect, useRef} from 'react';
import {FieldValues, useFormContext, UseFormReturn} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {getReactRoot} from 'utils/dom';

import {useRights} from './components/RightsContext';
import {
    fullRightCreate,
    fullRightParse,
    getAdminRights,
    getAttribute,
    getCheckboxes,
    nestedCheckboxKeys,
} from './helpers';
import {
    EmployeeRightsCreateBackendData,
    EmployeeRightsCreateFieldValues,
} from './types';

const useSetup = () => {
    const ready = useRef(false);

    if (ready.current) {
        return true;
    }
    ready.current = true;

    return false;
};

const getUpdatedKey = (
    oldData: Record<string, boolean>,
    newData: Record<string, boolean>,
) => {
    const data = uniq([...Object.keys(oldData), ...Object.keys(newData)]);

    return data.filter((key) => !isEqual(oldData[key], newData[key]));
};

const stripActionFromFullRightName = (name: string) => {
    const parts = name.split('.');
    parts.pop();

    return parts.join('.');
};

/**
 * Method takes the name of a nested right in the format "right.[Category]"
 * For example:
 * - rights.documents
 * - rights.order
 * - rights.bookkeeping
 */
const setNestedGranularRightValues = (
    methods: UseFormReturn,
    key: string,
    value: boolean,
) => {
    const reactRoot = getReactRoot();

    const isFullAccount =
        methods.getValues('user_license') === 'full_account_user';
    if (!isFullAccount) return;

    const nestedRights = nestedCheckboxKeys(
        reactRoot.querySelector(`div[id="${key}"]`),
    ).filter((k) => k !== key);

    nestedRights.forEach((right) => {
        methods.setValue(right, value);
    });
};

const useObjectTypeCanView = (categories: string[]) => {
    const setup = useSetup();

    const methods = useFormContext();
    const keys = categories.map((c) =>
        fullRightCreate('rights', c, 'can_view'),
    );
    methods.watch();

    const oldValues = useRef<Record<string, boolean>>({});
    const currrentValues: Record<string, boolean> = Object.fromEntries(
        keys.map((key) => [key, methods.getValues(key)]),
    );

    const updatedKeys = getUpdatedKey(oldValues.current, currrentValues);

    if (!setup) {
        oldValues.current = currrentValues;

        return;
    }

    if (updatedKeys.length > 0) {
        updatedKeys.forEach((key) => {
            oldValues.current[key] = currrentValues[key];
            setNestedGranularRightValues(
                methods,
                stripActionFromFullRightName(key),
                currrentValues[key],
            );
        });
    }
};

const useSpecialTimesheetLogic = () => {
    const methods = useFormContext();
    useEffect(() => {
        const root = getReactRoot();
        const element = root.querySelector(
            "[name='rights.timesheet.can_view']",
        );
        if (!element) return;

        const onClick = (event: Event) => {
            if (methods.getValues('time_tracking')) return;

            const checked = (event.target as HTMLInputElement)?.checked;

            if (methods.getValues('user_license') === 'full_account_user') {
                methods.setValue('rights.timesheet.can_view', checked);
                methods.setValue('rights.timesheet.can_create', checked);
                methods.setValue('rights.timesheet.can_cancel', checked);
                methods.setValue('rights.timesheet.can_send', checked);
            }
        };
        element.addEventListener('click', onClick);

        return () => {
            element.removeEventListener('click', onClick);
        };
    }, []);
};

const useCheckboxTriggers = () => {
    const {
        data: {objectCanViewProps, uiElements},
    } = useBackend<EmployeeRightsCreateBackendData>();
    const {hasRight} = useRights();
    const setup = useSetup();
    const methods = useFormContext();
    useObjectTypeCanView(objectCanViewProps);
    useSpecialTimesheetLogic();
    const reactRoot = getReactRoot();

    // Handle Administrator Checkbox
    const isAdministrator = methods.watch('is_admin');
    useEffect(() => {
        if (!setup) return;

        getAdminRights(uiElements).forEach((right) => {
            right.actions.forEach((action) => {
                methods.setValue(
                    fullRightCreate('rights', right.name, action.name),
                    isAdministrator,
                );
            });
        });
    }, [isAdministrator]);

    // Handle UserLicense Radio
    const isFullAccount = methods.watch('user_license') === 'full_account_user';
    useEffect(() => {
        if (!setup) return;

        const isSalesManagement = methods.getValues('sales_mgmt_license');
        if (!isSalesManagement) return;

        if (isFullAccount) {
            const documentApproval =
                reactRoot.querySelector('#document_approval');
            if (!documentApproval)
                throw new Error('Document Approval not found');

            const checkboxNames = getCheckboxes(documentApproval)
                .map(getAttribute('name'))
                .filter(Boolean) as string[];

            checkboxNames.forEach((name) => {
                methods.setValue(name, true);
            });

            /*
            Enabled the sub-checkboxes for rights which already hae an enabled parent
            If can_view for 'foo' is enabled then can_edit, can_create, can_delete should also be enabled 
            */
            const htmlSections = ['#documents', '#other', '#bookkeeping']
                .map((selector) => reactRoot.querySelector(selector))
                .filter(Boolean) as Element[];

            const enabledCanView = htmlSections
                .flatMap((element) => getCheckboxes(element))
                .map(getAttribute('name'))
                .filter((value) => value?.includes('can_view'))
                .filter(methods.getValues) as string[];

            const rightsToEnable = enabledCanView
                .map(fullRightParse)
                .map(({base, category}) => {
                    const value = `${base}.${category}`;

                    return reactRoot.querySelector(
                        `[id="${value}"]`,
                    ) as Element;
                })
                .filter(Boolean)
                .flatMap(getCheckboxes);

            rightsToEnable.forEach((element) => {
                const right = element.getAttribute('name');
                if (!right) return;

                if (hasRight(right)) {
                    methods.setValue(right, true);
                }
            });
        } else {
            const htmlSections = [
                '#document_approval',
                '#documents',
                '#other',
                '#bookkeeping',
            ]
                .map((selector) => reactRoot.querySelector(selector))
                .filter(Boolean) as Element[];

            const names = htmlSections
                .flatMap((element) => getCheckboxes(element))
                .map(getAttribute('name'))
                .filter(Boolean) as string[];

            const excludedNames = [
                'rights.timesheet.can_view',
                'rights.timesheet.can_create',
                'rights.timesheet.can_cancel',
            ];
            const filteredNames = names.filter(
                (name) =>
                    !name.includes('can_view') && !excludedNames.includes(name),
            );

            filteredNames.forEach((name) => {
                methods.setValue(name, false);
            });
        }

        // Timesheets require some custom functionality
        const timesheetCanView = methods.getValues('rights.timesheet.can_view');

        if (timesheetCanView) {
            if (!methods.getValues('time_tracking')) {
                methods.setValue('rights.timesheet.can_create', isFullAccount);
                methods.setValue('rights.timesheet.can_cancel', isFullAccount);
            }
            methods.setValue('rights.timesheet.can_send', isFullAccount);
        }
    }, [isFullAccount]);

    // Handle TimeTracking Checkbox
    const timeTracking = methods.watch('time_tracking');
    useEffect(() => {
        if (!setup) return;
        const isSalesManagement = methods.getValues('sales_mgmt_license');

        if (timeTracking) {
            methods.setValue('rights.timesheet.can_view', true);
            methods.setValue('rights.timesheet.can_create', true);
            methods.setValue('rights.timesheet.can_cancel', true);
        } else {
            if (methods.getValues('user_license') !== 'full_account_user') {
                methods.setValue('rights.timesheet.can_create', false);
                methods.setValue('rights.timesheet.can_cancel', false);
            }

            if (!isSalesManagement) {
                methods.setValue('rights.timesheet.can_view', false);
                methods.setValue('rights.timesheet.can_create', false);
                methods.setValue('rights.timesheet.can_cancel', false);
            }
        }
    }, [timeTracking]);

    // Handle SalesMgmtLicense Checkbox
    const salesMgmtLicense = methods.watch('sales_mgmt_license');
    useEffect(() => {
        if (!setup) return;

        if (methods.getValues('user_license') === 'full_account_user') {
            const htmlSections = [
                '#document_approval',
                '#documents',
                '#other',
                '#bookkeeping',
            ]
                .map((selector) => reactRoot.querySelector(selector))
                .filter(Boolean) as Element[];

            const excludeTimeTracking = [
                'rights.timesheet.can_view',
                'rights.timesheet.can_create',
                'rights.timesheet.can_cancel',
            ];
            const names = htmlSections
                .flatMap((element) => getCheckboxes(element))
                .map(getAttribute('name'))
                .filter((name) =>
                    methods.getValues('time_tracking') && name
                        ? !excludeTimeTracking.includes(name)
                        : true,
                )
                .filter(Boolean) as string[];

            names.forEach((right) => methods.setValue(right, salesMgmtLicense));
        } else {
            const htmlSections = [
                '#document_approval',
                '#documents',
                '#other',
                '#bookkeeping',
            ]
                .map((selector) => reactRoot.querySelector(selector))
                .filter(Boolean) as Element[];

            const excludeTimeTracking = [
                'rights.timesheet.can_view',
                'rights.timesheet.can_create',
                'rights.timesheet.can_cancel',
            ];
            const names = htmlSections
                .flatMap((element) => getCheckboxes(element))
                .map(getAttribute('name'))
                .filter((name) =>
                    methods.getValues('time_tracking') && name
                        ? !excludeTimeTracking.includes(name)
                        : true,
                )
                .filter(Boolean) as string[];

            names
                .filter((name) => name.includes('can_view'))
                .forEach((right) => methods.setValue(right, salesMgmtLicense));

            // timesheets require some custom functionality
            if (!methods.getValues('time_tracking')) {
                methods.setValue('rights.timesheet.can_view', salesMgmtLicense);
            }
        }
    }, [salesMgmtLicense]);
};

export default useCheckboxTriggers;

export const useResetOnMount = (
    methods: UseFormReturn<EmployeeRightsCreateFieldValues>,
    defaultValues: FieldValues,
) => {
    useEffect(() => {
        methods.reset(defaultValues);
    }, []);
};
